import React from 'react'
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  List,
  ListItem,
  ListIcon,
  Divider,
  Link,
  Icon,
  Alert,
  AlertIcon,
  Button,
} from '@chakra-ui/react'
import { LuDot, LuArrowLeft, LuFileText } from 'react-icons/lu'
import { Link as RouterLink, useParams, Navigate, useNavigate } from 'react-router-dom'
import { LEGAL_CONTENT, type LegalBlock } from './content'
import { LEGAL_DOCS, LEGAL_DRAFT, legalDocMeta } from '../../config/legal'
import { getSavedAuthData } from '../../api/axios/interceptor'
import { CHROME_HEADER_GRADIENT } from '../../theme/chrome'

/** Render **bold** spans inside an otherwise plain legal string. */
function renderInline(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <Text as="span" key={i} fontWeight="700" color="gray.800">
          {part.slice(2, -2)}
        </Text>
      )
    }
    return <React.Fragment key={i}>{part}</React.Fragment>
  })
}

function Block({ block }: { block: LegalBlock }) {
  if (block.kind === 'sub') {
    return (
      <Text fontSize="sm" fontWeight="700" color="gray.800" pt={1}>
        {renderInline(block.text)}
      </Text>
    )
  }
  if (block.kind === 'list') {
    return (
      <List spacing={2} pl={1}>
        {block.items.map((item, i) => (
          <ListItem key={i} fontSize="sm" color="gray.700" lineHeight="1.7" display="flex">
            <ListIcon as={LuDot} color="purple.400" mt="2px" boxSize={5} flexShrink={0} />
            <Box as="span">{renderInline(item)}</Box>
          </ListItem>
        ))}
      </List>
    )
  }
  return (
    <Text fontSize="sm" color="gray.700" lineHeight="1.75">
      {renderInline(block.text)}
    </Text>
  )
}

/**
 * Public, unauthenticated page rendering one legal document (CGU, CGV,
 * mentions légales, politique de confidentialité) from src/pages/Legal/content.
 * Route: /legal/:slug
 */
export default function LegalDocumentPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const meta = slug ? legalDocMeta(slug) : undefined
  const doc = meta ? LEGAL_CONTENT[meta.slug] : undefined

  // Smart back: return to the previous page when there is history (in-app
  // navigation); otherwise (e.g. opened in a new tab) go to a sensible home —
  // the app for an authenticated user, the login screen for a visitor.
  const handleBack = () => {
    const { userId } = getSavedAuthData()
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate(userId ? '/home' : '/login')
    }
  }

  if (!doc) {
    return <Navigate to="/legal/cgu" replace />
  }

  const formattedDate = (() => {
    try {
      return new Date(doc.effectiveDate).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    } catch {
      return doc.effectiveDate
    }
  })()

  return (
    <Box h="100dvh" overflowY="auto" bg="#F1EFEE" py={{ base: 6, md: 12 }}>
      <Container maxW="4xl">
        {/* Back / breadcrumb */}
        <HStack justify="space-between" mb={6} flexWrap="wrap" gap={2}>
          <Button
            onClick={handleBack}
            variant="ghost"
            size="sm"
            leftIcon={<Icon as={LuArrowLeft} />}
            color="gray.600"
          >
            Retour
          </Button>
          <HStack spacing={1} flexWrap="wrap">
            {LEGAL_DOCS.map((d) => (
              <Button
                key={d.slug}
                as={RouterLink}
                to={`/legal/${d.slug}`}
                size="xs"
                variant={d.slug === doc.slug ? 'solid' : 'ghost'}
                colorScheme={d.slug === doc.slug ? 'purple' : 'gray'}
              >
                {d.label}
              </Button>
            ))}
          </HStack>
        </HStack>

        <Box bg="white" borderRadius="2xl" boxShadow="sm" borderWidth="1px" borderColor="gray.200" overflow="hidden">
          {/* Header */}
          <Box
            bgGradient={CHROME_HEADER_GRADIENT}
            color="white"
            px={{ base: 6, md: 10 }}
            py={{ base: 6, md: 8 }}
          >
            <HStack spacing={3} mb={2}>
              <Box w="40px" h="40px" borderRadius="xl" bg="whiteAlpha.200" display="grid" sx={{ placeItems: 'center' }}>
                <Icon as={LuFileText} boxSize={5} />
              </Box>
              <Heading as="h1" fontSize={{ base: 'xl', md: '2xl' }} fontWeight="800" letterSpacing="tight">
                {doc.title}
              </Heading>
            </HStack>
            <Text fontSize="xs" color="whiteAlpha.800">
              Careflai · Version du {formattedDate}
            </Text>
          </Box>

          <Box px={{ base: 6, md: 10 }} py={{ base: 6, md: 8 }}>
            {LEGAL_DRAFT && (
              <Alert status="warning" borderRadius="lg" mb={6} fontSize="xs" alignItems="flex-start">
                <AlertIcon />
                <Box>
                  <Text fontWeight="700">Projet en cours de validation juridique</Text>
                  <Text>
                    Ce document est une version préparatoire. Il sera finalisé après relecture par un
                    conseil juridique et le délégué à la protection des données.
                  </Text>
                </Box>
              </Alert>
            )}

            <Text fontSize="sm" color="gray.600" lineHeight="1.75" mb={6} fontStyle="italic">
              {doc.intro}
            </Text>

            <VStack align="stretch" spacing={7}>
              {doc.sections.map((section, i) => (
                <Box key={i}>
                  <Heading as="h2" fontSize="md" fontWeight="700" color="gray.900" mb={3}>
                    {section.heading}
                  </Heading>
                  <VStack align="stretch" spacing={3}>
                    {section.blocks.map((block, j) => (
                      <Block key={j} block={block} />
                    ))}
                  </VStack>
                </Box>
              ))}
            </VStack>

            <Divider my={8} />
            <Text fontSize="xs" color="gray.500">
              Pour toute question relative à ce document, contactez-nous. Voir aussi les autres
              documents légaux ci-dessus.
            </Text>
            <HStack spacing={3} mt={2} flexWrap="wrap">
              {LEGAL_DOCS.filter((d) => d.slug !== doc.slug).map((d) => (
                <Link as={RouterLink} key={d.slug} to={`/legal/${d.slug}`} fontSize="xs" color="purple.600">
                  {d.label}
                </Link>
              ))}
            </HStack>
          </Box>
        </Box>
      </Container>
    </Box>
  )
}
